import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { Flex, Modal, Typography } from 'antd';
import { NotificationContext } from '@Context/NotificationContext';
import { MButton, MTextArea } from '@Components/Base';

import styles from './required-notification.module.css';

interface RequiredNotificationProps {
  title: string;
  message: string;
  textSuccessBtn: string;
  isModalOpen: boolean;
  handleSuccess: () => void;
  handleCancel: () => void;
  isSuccessIcon?: boolean;
  textCancelBtn?: string;
  isDeleteNotification?: boolean;
  handleCommentChange?: (value: string) => void;
}

const RequiredNotification: React.FC<RequiredNotificationProps> = ({
  title,
  message,
  isModalOpen,
  textSuccessBtn,
  textCancelBtn = 'No, Thanks',
  handleSuccess,
  handleCancel,
  isDeleteNotification = false,
  handleCommentChange,
}) => {
  const { errorNotification } = useContext(NotificationContext);
  const [commentValue, setCommentValue] = useState('');
  const [isError, setIsError] = useState(false);

  const onCancel = (): void => {
    setIsError(false);
    handleCancel();
  };

  const onSuccess = useCallback((): void => {
    if (handleCommentChange && commentValue === '') {
      setIsError(true);
      errorNotification('Please leave a comment');
      return;
    }

    handleSuccess();
  }, [handleCommentChange, commentValue, handleSuccess]);

  const onCommentChange = useCallback(
    (value: string): void => {
      if (handleCommentChange) {
        handleCommentChange(value);
        setCommentValue(value);
      }

      if (isError && value !== '') {
        setIsError(false);
      }
    },
    [handleCommentChange, isError]
  );

  const customButtons = useMemo(() => {
    if (isDeleteNotification) {
      return [
        <MButton key="back" type="tertiary" onClick={onCancel}>
          {textCancelBtn}
        </MButton>,
        <MButton key="submit" danger onClick={onSuccess}>
          {textSuccessBtn}
        </MButton>,
      ];
    }

    return [
      <MButton key="back" type="tertiary" onClick={onCancel}>
        {textCancelBtn}
      </MButton>,
      <MButton key="submit" type="primary" onClick={onSuccess}>
        {textSuccessBtn}
      </MButton>,
    ];
  }, [
    textCancelBtn,
    textSuccessBtn,
    handleSuccess,
    handleCancel,
    isDeleteNotification,
  ]);

  return (
    <Modal
      open={isModalOpen}
      onOk={onSuccess}
      onCancel={onCancel}
      className={styles.modal}
      footer={customButtons}
      closeIcon={false}
    >
      <Flex vertical>
        <Flex className={styles.titleSection}>
          <Typography.Text className={styles.title}>{title}</Typography.Text>
        </Flex>
        <div className={styles.messageSection}>{message}</div>
        {handleCommentChange && (
          <MTextArea
            className={styles.textArea}
            placeholder="Leave a comment"
            status={isError ? 'error' : ''}
            onChange={(event) => {
              onCommentChange(event.target.value);
            }}
            showCount
            style={{ height: 120 }}
            maxLength={100}
          />
        )}
      </Flex>
    </Modal>
  );
};

export default memo(RequiredNotification);
